import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import {
  getProfessionalHours,
  saveProfessionalHours
} from "../../services/professionalHours.api";

/**
 * Editor de la disponibilidad semanal de un profesional (doc 0396 · M0.4.10).
 *
 * Modelo de franjas y no de "hora de entrada / hora de salida": un profesional
 * con consulta por la mañana y quirófano por la tarde declara dos bloques, y el
 * descanso es sencillamente el hueco entre ambos.
 *
 * La numeración de los días es ISO —1=Lunes … 7=Domingo— y coincide con la que
 * usa el resto del panel. Es la misma convención que el backend valida.
 */
const DAYS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
  { value: 7, label: "Domingo" }
];

const toInputTime = (value) => (value ? String(value).slice(0, 5) : "");

const professionalShape = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string
});

function ProfHoursDialog({ open, professional, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slots, setSlots] = useState([]);
  const [isConfigured, setIsConfigured] = useState(true);

  const load = useCallback(async () => {
    if (!professional?.id) return;
    setLoading(true);
    try {
      const { data } = await getProfessionalHours(professional.id);
      setSlots(
        (data.slots || []).map((s) => ({
          dayOfWeek: s.dayOfWeek,
          startTime: toInputTime(s.startTime),
          endTime: toInputTime(s.endTime)
        }))
      );
      setIsConfigured(Boolean(data.isConfigured));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [professional]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const addSlot = (dayOfWeek) =>
    setSlots((prev) => [
      ...prev,
      { dayOfWeek, startTime: "09:00", endTime: "18:00" }
    ]);

  const removeSlot = (index) =>
    setSlots((prev) => prev.filter((_, i) => i !== index));

  const updateSlot = (index, field, value) =>
    setSlots((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    );

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveProfessionalHours(professional.id, slots);
      toast.success("Horario guardado");
      onClose(true);
    } catch (err) {
      // El backend distingue los motivos: formato inválido, fin anterior al
      // inicio y franjas que se pisan llegan con su propio mensaje, así que se
      // muestra tal cual en lugar de un "no se pudo guardar" genérico.
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        Horario de {professional?.name || "profesional"}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          Declara los bloques en los que atiende. Un día sin bloques significa que
          no trabaja ese día.
        </Typography>

        {!isConfigured && !loading && (
          <Typography
            variant="body2"
            style={{ marginBottom: 12, fontStyle: "italic" }}
            color="textSecondary"
          >
            Este profesional aún no tiene horario propio: hereda el horario de
            atención del negocio.
          </Typography>
        )}

        {loading ? (
          <Box style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          DAYS.map((day) => {
            const dayIndexes = slots
              .map((s, i) => ({ ...s, index: i }))
              .filter((s) => s.dayOfWeek === day.value);

            return (
              <Paper
                key={day.value}
                elevation={0}
                style={{ padding: 12, marginBottom: 8, background: "transparent" }}
              >
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: dayIndexes.length ? 8 : 0
                  }}
                >
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    {day.label}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addSlot(day.value)}
                  >
                    Agregar bloque
                  </Button>
                </Box>

                {dayIndexes.length === 0 ? (
                  <Typography variant="caption" color="textSecondary">
                    No atiende
                  </Typography>
                ) : (
                  dayIndexes.map((slot) => (
                    <Box
                      key={slot.index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 6
                      }}
                    >
                      <TextField
                        type="time"
                        size="small"
                        variant="outlined"
                        value={slot.startTime}
                        onChange={(e) =>
                          updateSlot(slot.index, "startTime", e.target.value)
                        }
                        inputProps={{ step: 300 }}
                      />
                      <Typography variant="body2">a</Typography>
                      <TextField
                        type="time"
                        size="small"
                        variant="outlined"
                        value={slot.endTime}
                        onChange={(e) =>
                          updateSlot(slot.index, "endTime", e.target.value)
                        }
                        inputProps={{ step: 300 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeSlot(slot.index)}
                        title="Quitar bloque"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))
                )}
              </Paper>
            );
          })
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving || loading}
        >
          {saving ? "Guardando…" : "Guardar horario"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ProfHoursDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  /** `null` mientras el diálogo está cerrado: no hay profesional seleccionado. */
  professional: professionalShape,
  /** Recibe `true` si se guardó, para que el llamador decida si refresca. */
  onClose: PropTypes.func.isRequired
};

ProfHoursDialog.defaultProps = {
  professional: null
};

export default ProfHoursDialog;
